// © Microsoft Corporation. All rights reserved.

import { ObjectTelemetryContract, Telemetry, TelemetryObjectBase, TelemetryType } from "@msnews/telemetry-contracts";

import { oneServiceHeaders } from "../constants/Constants";

/**
 * Try creating and adding a child telemetry object to the parent object.
 * This is useful when you you have a list of children that will be loaded asynchronously.
 * Loading children asynchronously has the detrimental effect that telemetry ordinal IDs will
 * be non deterministically generated (as they are usually generated via in increment on the number
 * of existing children, and that is incremented when the child's onComponentConnect is called, which
 * could be at any time).
 * @returns the created child telemetry object.
 */
export function tryAddOrderedTelemetryChild(parent: TelemetryObjectBase, childExperienceType: string): TelemetryObjectBase | null {
    if (parent) {
        const telemetryContract = {
            type: TelemetryType.Module,
            name: childExperienceType,
            content: {}
        } as ObjectTelemetryContract;

        return parent.addOrUpdateChild(telemetryContract);
    }

    return null;
}

/**
 * Helper to get the ocid for the page
 * @returns - the ocid if avaliable, else undefined
 */
export function getOcid(): string | undefined {
    if (!Telemetry) {
        return;
    }

    const pageMetadata = Telemetry.getPageMetadata();

    if (!pageMetadata || !pageMetadata.contract || !pageMetadata.contract.page) {
        return;
    }

    return pageMetadata.contract.page.ocid;
}

/**
 * Appends page ocid into given URL
 * @param url - destination URL
 * @returns the modified URL if ocid not added
 */
export function appendOcid(url: string): string {
    const ocid = getOcid();
    const lowerCaseUrl = url.toLowerCase();
    if (!ocid || lowerCaseUrl.indexOf("?ocid=") != -1 || lowerCaseUrl.indexOf("&ocid=") != -1) {
        return url;
    }
    const token = (url.indexOf("?") == -1) ? "?" : "&";
    return `${url}${token}ocid=${encodeURIComponent(ocid)}`;
}

/**
 * Masks sensitive data from headers for logging purposes eg. user tokens
 * @param requestHeaders Headers used in request
 * @returns HeadersInit headers with sensitive data masked
 */
export function maskHeadersForLogging(requestHeaders: HeadersInit): HeadersInit {
    if (!requestHeaders) {
        return null;
    }

    const headersToLog = {
        ...requestHeaders
    };

    if (headersToLog[oneServiceHeaders.entAuthorization]) {
        headersToLog[oneServiceHeaders.entAuthorization] = "*****";
    }

    if (headersToLog[oneServiceHeaders.rpsToken]) {
        headersToLog[oneServiceHeaders.rpsToken] = "*****";
    }

    return headersToLog;
}